import type { UploadUrlResponse } from '@manasil/shared';
import { api, resolveMediaUrl } from './api.js';

/**
 * Audio goes straight from this browser to GCS. The API only issues the signed URL and
 * verifies afterwards that the object landed — it never sees the bytes.
 */

/**
 * The API accepts exactly four content types. Browsers and operating systems report far
 * more names than that for the same file, so both the reported MIME type and the file
 * extension are mapped onto one of the four.
 */
const CONTENT_TYPES: Record<string, string> = {
  // MP3 — this is what "MPEG audio" means in practice, and the usual delivery format.
  'audio/mpeg': 'audio/mpeg',
  'audio/mp3': 'audio/mpeg',
  'audio/x-mp3': 'audio/mpeg',
  'audio/x-mpeg': 'audio/mpeg',
  'audio/mpeg3': 'audio/mpeg',
  'audio/x-mpeg-3': 'audio/mpeg',
  'audio/mpg': 'audio/mpeg',
  // AAC in an MP4 container
  'audio/mp4': 'audio/mp4',
  'audio/m4a': 'audio/mp4',
  'audio/x-m4a': 'audio/mp4',
  // raw AAC
  'audio/aac': 'audio/aac',
  'audio/aacp': 'audio/aac',
  'audio/x-aac': 'audio/aac',
  // WAV
  'audio/wav': 'audio/wav',
  'audio/wave': 'audio/wav',
  'audio/x-wav': 'audio/wav',
  'audio/vnd.wave': 'audio/wav',
};

/** Used when the browser reports no type at all, which happens more often than you would think. */
const EXTENSIONS: Record<string, string> = {
  mp3: 'audio/mpeg',
  mpeg: 'audio/mpeg',
  mpga: 'audio/mpeg',
  mp2: 'audio/mpeg',
  m4a: 'audio/mp4',
  m4b: 'audio/mp4',
  aac: 'audio/aac',
  wav: 'audio/wav',
  wave: 'audio/wav',
};

/** What the file picker offers, so unsupported files are hard to choose in the first place. */
export const ACCEPT_ATTRIBUTE = [
  ...new Set(Object.keys(CONTENT_TYPES)),
  ...Object.keys(EXTENSIONS).map((ext) => `.${ext}`),
].join(',');

export function normaliseContentType(file: File): string | null {
  const reported = file.type.toLowerCase().split(';')[0]?.trim() ?? '';
  const direct = CONTENT_TYPES[reported];
  if (direct) return direct;

  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return EXTENSIONS[ext] ?? null;
}

/** Names what was actually seen — "unsupported file" on its own is useless to whoever hit it. */
function unsupportedMessage(file: File): string {
  const ext = file.name.split('.').pop()?.toLowerCase();
  const seen = file.type || (ext ? `.${ext}` : 'an unknown type');
  return `Manasil takes MP3, M4A, AAC or WAV audio — this file looks like ${seen}. If it is an MPEG video, export just the audio track as MP3 first.`;
}

/**
 * The real duration, read by decoding the file locally. The server stores what we send
 * here because only the browser has decoded the audio — but it independently verifies the
 * object's existence and size, so a wrong duration is the worst a client can cause.
 */
export function probeDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    audio.preload = 'metadata';

    const done = (fn: () => void) => {
      URL.revokeObjectURL(url);
      fn();
    };

    audio.onloadedmetadata = () => {
      const seconds = audio.duration;
      if (!Number.isFinite(seconds) || seconds <= 0) {
        done(() => reject(new Error('Could not read the length of that file')));
        return;
      }
      done(() => resolve(Math.round(seconds)));
    };
    audio.onerror = () => done(() => reject(new Error('That file could not be decoded as audio')));
    audio.src = url;
  });
}

/** XHR rather than fetch, because fetch cannot report upload progress. */
export function put(
  url: string,
  file: File,
  headers: Record<string, string>,
  onProgress: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    for (const [key, value] of Object.entries(headers)) xhr.setRequestHeader(key, value);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(1);
        resolve();
        return;
      }
      reject(new Error(`Upload rejected by storage (${xhr.status})`));
    };
    xhr.onerror = () =>
      reject(
        new Error(
          'Upload could not reach storage. Check the API is running, and — once audio moves to a GCS bucket — that the bucket allows PUT from this origin.',
        ),
      );
    xhr.onabort = () => reject(new Error('Upload cancelled'));
    xhr.send(file);
  });
}

/** The whole three-step flow: sign → PUT → finalize. */
export async function uploadStoryAudio(
  storyId: string,
  file: File,
  onProgress: (fraction: number) => void,
): Promise<void> {
  const contentType = normaliseContentType(file);
  if (!contentType) throw new Error(unsupportedMessage(file));

  // Read the duration first — a file we cannot decode should never reach the bucket.
  const durationSec = await probeDuration(file);

  const signed = await api<UploadUrlResponse>(`/admin/stories/${storyId}/upload-url`, {
    method: 'POST',
    body: { contentType, sizeBytes: file.size },
  });

  await put(resolveMediaUrl(signed.uploadUrl), file, signed.headers, onProgress);

  await api(`/admin/stories/${storyId}/finalize`, {
    method: 'POST',
    body: { durationSec },
  });
}
